import Menu from 'antd/lib/menu';
import Popconfirm from 'antd/lib/popconfirm';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Typography from 'antd/lib/typography';
import {
    CaretRightOutlined,
    PauseOutlined,
    PercentageOutlined,
    PlayCircleFilled,
    RiseOutlined,
} from '@ant-design/icons';
import * as React from 'react';
import {
    getIsAppleAppStore,
    getIsGooglePlayStore,
    getIsHuaweiAppGallery,
    getRolloutDaysToPercentageFromAutomation,
    isAppPlatform,
    isPhasedReleaseComplete,
    isPhasedReleasePaused,
} from '../../helpers';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import {
    AppleAppStorePhasedReleaseState,
    AppPlatform,
    AppStoreType,
    AutomationAutoStagedRollout,
    GooglePlayStoreState,
    HuaweiPhasedReleaseState,
    IntegrationType,
    PhasedRelease,
    RolloutAction,
    PhasedReleaseState,
    RunwayResponseType,
} from '../../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';
import IntegrationIcon from '../icons/IntegrationIcon';
import { RolloutActionButtons } from './RolloutActionButtons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { ReleaseUpdatable } from '../../actions/updatable';
import {
    BaseUpdateReleaseRequest,
    tryUpdateRelease,
    UpdateApplePhasedReleaseRequestReleaseStep,
    UpdateGooglePhasedReleaseRequestReleaseStep,
    UpdateHuaweiPhasedReleaseRequestReleaseStep,
    UpdatePhasedReleaseRequestBaseReleaseStep,
} from '../../actions/release';
import UpdateStagedRolloutModal from '../UpdateStagedRolloutModal';
import { selectAutomationById, selectUserCanPerformAction } from '../../selectors/selectors';
import { globalMessage } from '../../utils/globalMessage';
import PhasedReleaseDayText from '../phasedReleaseModule/PhasedReleaseDayText';
import { MonitoringProgressCard } from './MonitoringProgressCard';
import { capitalize } from 'lodash';
import {
    convertAndroidStagedRolloutPercentage,
    convertUserFraction,
    displayStringForRolloutPercentage,
    displayTextForPhasedReleaseState,
    phasedReleaseDisplayTextForAppStoreType,
    getRolloutPercentText,
} from '../../helpers/displayHelpers';
import { formatErrorMessageForDisplay } from '../../helpers';
import Tooltip from 'antd/lib/tooltip';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { StepRunUpdatable } from '@/actions/stepRun';
import Flex from 'antd/lib/flex';
import { PhasedReleaseChartBase } from '../appHome/widgets/LiveReleaseChartWidget';

export const formatResumedAtString = (resumedAt?: string) => {
    return resumedAt ?
            `${dayjs(resumedAt).calendar(null, {
                sameDay: '[Today] [at] ' + DatetimeFormat.ShortTime,
                nextDay: '[Tomorrow] [at] ' + DatetimeFormat.ShortTime,
                lastDay: DatetimeFormat.MonthAbbreviatedDateTime,
                lastWeek: DatetimeFormat.MonthAbbreviatedDateTime,
                nextWeek: DatetimeFormat.MonthAbbreviatedDateTime,
                sameElse: DatetimeFormat.MonthAbbreviatedDateTime,
            })}`
        :   'at the end of the blackout period';
};

export const PausedByBlackoutBadge: React.FC<{
    phasedRelease: PhasedRelease;
    isSizeSmall?: boolean;
}> = ({ phasedRelease, isSizeSmall }) => {
    const outerSize = isSizeSmall ? 14 : 16;
    const innerSize = isSizeSmall ? 8 : 10;

    const resumedAtString = formatResumedAtString(phasedRelease.blackoutEndsAt);
    return (
        <Tooltip title={`Paused due to a scheduled blackout period. Rollout will be resumed ${resumedAtString}.`}>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: runwayColors.statusYellow,
                    height: outerSize,
                    width: outerSize,
                    borderRadius: outerSize / 2,
                    flexShrink: 0,
                }}
            >
                <PauseOutlined style={{ color: runwayColors.white, fontSize: innerSize }} />
            </div>
        </Tooltip>
    );
};

const PhasedReleaseCardFull: React.FC<PhasedReleaseCardProps> = (props: PhasedReleaseCardProps) => {
    const {
        orgId,
        appId,
        releaseId,
        platform,
        appStoreType,
        phasedRelease,
        appStoreIntegrationId,
        autoStagedRolloutAutomationId,
        stepRunComponentProps,
    } = props;

    const dispatch = useDispatch();

    const [stagedRolloutEditModalVisible, setStagedRolloutEditModalVisible] = React.useState(false);
    const [autoSelectAutomations, setAutoSelectAutomations] = React.useState(false);

    const rolloutStartedAt = props.rolloutStartedAt;

    const autoStagedRolloutAutomation = useSelector((state: RootState) => {
        return selectAutomationById(state, props.appId, autoStagedRolloutAutomationId);
    }) as AutomationAutoStagedRollout;

    const automatedStagedRolloutEnabled = autoStagedRolloutAutomation?.enabled;

    const daysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const [
        pauseGoogleStagedRelease,
        pauseGoogleStagedReleaseIsLoading,
        pauseGoogleStagedReleaseResponseType,
        pauseGoogleStagedReleaseError,
    ] = useCallUpdatable(StepRunUpdatable.PauseGooglePhasedRelease, props.stepRunComponentProps);

    const haltGoogleStagedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.HaltGooglePhasedRelease,
        releaseId,
    );
    const haltGoogleStagedReleaseErrorReleaseStep = useError(ReleaseUpdatable.HaltGooglePhasedRelease, releaseId);
    const haltGoogleStagedReleaseSuccessReleaseStep = useUpdated(ReleaseUpdatable.HaltGooglePhasedRelease, releaseId);

    const haltGoogleStagedReleaseIsLoading =
        props.stepRunComponentProps ? pauseGoogleStagedReleaseIsLoading : haltGoogleStagedReleaseIsLoadingReleaseStep;
    const haltGoogleStagedReleaseError =
        props.stepRunComponentProps ? pauseGoogleStagedReleaseError : haltGoogleStagedReleaseErrorReleaseStep;
    const haltGoogleStagedReleaseSuccess =
        props.stepRunComponentProps ?
            pauseGoogleStagedReleaseResponseType === RunwayResponseType.Success
        :   haltGoogleStagedReleaseSuccessReleaseStep;

    const [
        completeGoogleStagedReleaseStepRun,
        completeGoogleStagedReleaseIsLoadingStepRun,
        completeGoogleStagedReleaseResponseTypeStepRun,
        completeGoogleStagedReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.CompleteGooglePhasedRelease, props.stepRunComponentProps);

    const completeGoogleStagedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.CompleteGooglePhasedRelease,
        releaseId,
    );
    const completeGoogleStagedReleaseErrorReleaseStep = useError(
        ReleaseUpdatable.CompleteGooglePhasedRelease,
        releaseId,
    );
    const completeGoogleStagedReleaseSuccessReleaseStep = useUpdated(
        ReleaseUpdatable.CompleteGooglePhasedRelease,
        releaseId,
    );

    const completeGoogleStagedReleaseIsLoading =
        props.stepRunComponentProps ?
            completeGoogleStagedReleaseIsLoadingStepRun
        :   completeGoogleStagedReleaseIsLoadingReleaseStep;
    const completeGoogleStagedReleaseError =
        props.stepRunComponentProps ?
            completeGoogleStagedReleaseErrorStepRun
        :   completeGoogleStagedReleaseErrorReleaseStep;
    const completeGoogleStagedReleaseSuccess =
        props.stepRunComponentProps ?
            completeGoogleStagedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   completeGoogleStagedReleaseSuccessReleaseStep;

    const [
        pauseApplePhasedReleaseStepRun,
        pauseApplePhasedReleaseIsLoadingStepRun,
        pauseApplePhasedReleaseResponseTypeStepRun,
        pauseApplePhasedReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.PauseApplePhasedRelease, props.stepRunComponentProps);

    const pauseApplePhasedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.PauseApplePhasedRelease,
        releaseId,
    );
    const pauseApplePhasedReleaseErrorReleaseStep = useError(ReleaseUpdatable.PauseApplePhasedRelease, releaseId);
    const pauseApplePhasedReleaseSuccessReleaseStep = useUpdated(ReleaseUpdatable.PauseApplePhasedRelease, releaseId);

    const pauseApplePhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            pauseApplePhasedReleaseIsLoadingStepRun
        :   pauseApplePhasedReleaseIsLoadingReleaseStep;
    const pauseApplePhasedReleaseError =
        props.stepRunComponentProps ? pauseApplePhasedReleaseErrorStepRun : pauseApplePhasedReleaseErrorReleaseStep;
    const pauseApplePhasedReleaseSuccess =
        props.stepRunComponentProps ?
            pauseApplePhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   pauseApplePhasedReleaseSuccessReleaseStep;

    const [
        resumeApplePhasedReleaseStepRun,
        resumeApplePhasedReleaseIsLoadingStepRun,
        resumeApplePhasedReleaseResponseTypeStepRun,
        resumeApplePhasedReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.ResumeApplePhasedRelease, props.stepRunComponentProps);

    const resumeApplePhasedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.ResumeApplePhasedRelease,
        releaseId,
    );
    const resumeApplePhasedReleaseErrorReleaseStep = useError(ReleaseUpdatable.ResumeApplePhasedRelease, releaseId);
    const resumeApplePhasedReleaseSuccessReleaseStep = useUpdated(ReleaseUpdatable.ResumeApplePhasedRelease, releaseId);

    const resumeApplePhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            resumeApplePhasedReleaseIsLoadingStepRun
        :   resumeApplePhasedReleaseIsLoadingReleaseStep;
    const resumeApplePhasedReleaseError =
        props.stepRunComponentProps ? resumeApplePhasedReleaseErrorStepRun : resumeApplePhasedReleaseErrorReleaseStep;
    const resumeApplePhasedReleaseSuccess =
        props.stepRunComponentProps ?
            resumeApplePhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   resumeApplePhasedReleaseSuccessReleaseStep;

    const [
        completeApplePhasedReleaseStepRun,
        completeApplePhasedReleaseIsLoadingStepRun,
        completeApplePhasedReleaseResponseTypeStepRun,
        completeApplePhasedReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.CompleteApplePhasedRelease, props.stepRunComponentProps);

    const completeApplePhasedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.CompleteApplePhasedRelease,
        releaseId,
    );
    const completeApplePhasedReleaseErrorReleaseStep = useError(ReleaseUpdatable.CompleteApplePhasedRelease, releaseId);
    const completeApplePhasedReleaseSuccessReleaseStep = useUpdated(
        ReleaseUpdatable.CompleteApplePhasedRelease,
        releaseId,
    );

    const completeApplePhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            completeApplePhasedReleaseIsLoadingStepRun
        :   completeApplePhasedReleaseIsLoadingReleaseStep;
    const completeApplePhasedReleaseError =
        props.stepRunComponentProps ?
            completeApplePhasedReleaseErrorStepRun
        :   completeApplePhasedReleaseErrorReleaseStep;
    const completeApplePhasedReleaseSuccess =
        props.stepRunComponentProps ?
            completeApplePhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   completeApplePhasedReleaseSuccessReleaseStep;

    const [
        completeHuaweiPhasedReleaseStepRun,
        completeHuaweiPhasedReleaseIsLoadingStepRun,
        completeHuaweiPhasedReleaseResponseTypeStepRun,
        completeHuaweiPhasedReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.CompleteHuaweiPhasedRelease, props.stepRunComponentProps);

    const completeHuaweiPhasedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.CompleteHuaweiPhasedRelease,
        releaseId,
    );
    const completeHuaweiPhasedReleaseErrorReleaseStep = useError(
        ReleaseUpdatable.CompleteHuaweiPhasedRelease,
        releaseId,
    );
    const completeHuaweiPhasedReleaseSuccessReleaseStep = useUpdated(
        ReleaseUpdatable.CompleteHuaweiPhasedRelease,
        releaseId,
    );

    const completeHuaweiPhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            completeHuaweiPhasedReleaseIsLoadingStepRun
        :   completeHuaweiPhasedReleaseIsLoadingReleaseStep;
    const completeHuaweiPhasedReleaseError =
        props.stepRunComponentProps ?
            completeHuaweiPhasedReleaseErrorStepRun
        :   completeHuaweiPhasedReleaseErrorReleaseStep;
    const completeHuaweiPhasedReleaseSuccess =
        props.stepRunComponentProps ?
            completeHuaweiPhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   completeHuaweiPhasedReleaseSuccessReleaseStep;

    const [
        pauseHuaweiPhasedReleaseStepRun,
        pauseHuaweiPhasedReleaseIsLoadingStepRun,
        pauseHuaweiPhasedReleaseResponseTypeStepRun,
        pauseHuaweiPhasedReleaseErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.PauseHuaweiPhasedRelease, props.stepRunComponentProps);

    const pauseHuaweiPhasedReleaseIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.PauseHuaweiPhasedRelease,
        releaseId,
    );
    const pauseHuaweiPhasedReleaseErrorReleaseStep = useError(ReleaseUpdatable.PauseHuaweiPhasedRelease, releaseId);
    const pauseHuaweiPhasedReleaseSuccessReleaseStep = useUpdated(ReleaseUpdatable.PauseHuaweiPhasedRelease, releaseId);
    const pauseHuaweiPhasedReleaseIsLoading =
        props.stepRunComponentProps ?
            pauseHuaweiPhasedReleaseIsLoadingStepRun
        :   pauseHuaweiPhasedReleaseIsLoadingReleaseStep;
    const pauseHuaweiPhasedReleaseError =
        props.stepRunComponentProps ? pauseHuaweiPhasedReleaseErrorStepRun : pauseHuaweiPhasedReleaseErrorReleaseStep;
    const pauseHuaweiPhasedReleaseSuccess =
        props.stepRunComponentProps ?
            pauseHuaweiPhasedReleaseResponseTypeStepRun === RunwayResponseType.Success
        :   pauseHuaweiPhasedReleaseSuccessReleaseStep;

    const [
        startStagedRolloutStepRun,
        startStagedRolloutIsLoadingStepRun,
        startStagedRolloutResponseTypeStepRun,
        startStagedRolloutErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.StartAutomatedAndroidStagedRollout, props.stepRunComponentProps);

    const startStagedRolloutIsLoadingReleaseStep = useIsLoading(
        ReleaseUpdatable.StartAutomatedStagedRollout,
        releaseId,
    );
    const startStagedRolloutErrorReleaseStep = useError(ReleaseUpdatable.StartAutomatedStagedRollout, releaseId);
    const startStagedRolloutSuccessReleaseStep = useUpdated(ReleaseUpdatable.StartAutomatedStagedRollout, releaseId);

    const startStagedRolloutIsLoading =
        props.stepRunComponentProps ? startStagedRolloutIsLoadingStepRun : startStagedRolloutIsLoadingReleaseStep;
    const startStagedRolloutError =
        props.stepRunComponentProps ? startStagedRolloutErrorStepRun : startStagedRolloutErrorReleaseStep;
    const startStagedRolloutSuccess =
        props.stepRunComponentProps ?
            startStagedRolloutResponseTypeStepRun === RunwayResponseType.Success
        :   startStagedRolloutSuccessReleaseStep;

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isAppleAppStore = getIsAppleAppStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);

    const startAutomatedStagedRollout = () => {
        const request: BaseUpdateReleaseRequest = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };

        if (props.stepRunComponentProps) {
            startStagedRolloutStepRun();
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.StartAutomatedStagedRollout, request, releaseId));
        }
    };

    const haltGoogleStagedRollout = () => {
        const request: UpdateGooglePhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            userFraction: 0,
            phaseState: GooglePlayStoreState.Halted,
            actionType: RolloutAction.Halt,
        };

        if (props.stepRunComponentProps) {
            pauseGoogleStagedRelease(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.HaltGooglePhasedRelease, request, releaseId));
        }
    };

    const haltHuaweiPhasedRelease = () => {
        const request: UpdateHuaweiPhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            userFraction: 0,
            phaseState: HuaweiPhasedReleaseState.Suspended,
            actionType: RolloutAction.Halt,
        };

        if (props.stepRunComponentProps) {
            pauseHuaweiPhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PauseHuaweiPhasedRelease, request, props.releaseId));
        }
    };

    const completeGoogleStagedRollout = () => {
        const request: UpdateGooglePhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            userFraction: 1,
            phaseState: GooglePlayStoreState.Completed,
            actionType: RolloutAction.UpdatePercentage,
        };

        if (props.stepRunComponentProps) {
            completeGoogleStagedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.CompleteGooglePhasedRelease, request, releaseId));
        }
    };

    const completeHuaweiPhasedRelease = () => {
        const request: UpdateHuaweiPhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            userFraction: 1,
            phaseState: HuaweiPhasedReleaseState.Released,
            actionType: RolloutAction.UpdatePercentage,
        };

        if (props.stepRunComponentProps) {
            completeHuaweiPhasedReleaseStepRun(request);
        } else {
            const releaseStepRequest: UpdatePhasedReleaseRequestBaseReleaseStep = { ...request };
            dispatch(tryUpdateRelease(ReleaseUpdatable.CompleteHuaweiPhasedRelease, releaseStepRequest, releaseId));
        }
    };

    const pauseApplePhasedRelease = () => {
        const request: UpdateApplePhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            phaseState: AppleAppStorePhasedReleaseState.Paused,
            actionType: RolloutAction.Halt,
            userFraction: null,
        };

        if (props.stepRunComponentProps) {
            pauseApplePhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.PauseApplePhasedRelease, request, releaseId));
        }
    };

    const resumeApplePhasedRelease = () => {
        const request: UpdateApplePhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            phaseState: AppleAppStorePhasedReleaseState.Active,
            actionType: RolloutAction.Resume,
            resumeAutomations: [],
            userFraction: null,
        };

        if (props.stepRunComponentProps) {
            resumeApplePhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.ResumeApplePhasedRelease, request, releaseId));
        }
    };

    const completeApplePhasedRelease = () => {
        const request: UpdateApplePhasedReleaseRequestReleaseStep = {
            orgId,
            appId,
            releaseId,
            phaseState: AppleAppStorePhasedReleaseState.Complete,
            actionType: RolloutAction.UpdatePercentage,
            userFraction: null,
        };

        if (props.stepRunComponentProps) {
            completeApplePhasedReleaseStepRun(request);
        } else {
            dispatch(tryUpdateRelease(ReleaseUpdatable.CompleteApplePhasedRelease, request, releaseId));
        }
    };

    const isAutoHalted = phasedRelease?.isRunwayPaused;
    const isAutoAccelerated = phasedRelease?.isRunwayAcceleratedRollout;

    const phasedReleasePaused = isPhasedReleasePaused(phasedRelease?.state);
    const rolloutComplete = isPhasedReleaseComplete(phasedRelease);

    const hasPhasedReleaseState = !!phasedRelease;
    const phasedStateStatusText = displayTextForPhasedReleaseState(
        phasedRelease?.state,
        phasedRelease?.isManuallyAcceleratedPhasedRollout,
        isAutoAccelerated,
        isAutoHalted,
    );
    const phasedStateColor = colorForPhasedState(phasedRelease?.state);

    // for iOS, just whether a phased release is present. For android, whether they've enabled automated rollouts
    const isPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    const userFraction =
        isGooglePlayStore ? phasedRelease?.userFraction
        : isAppleAppStore ?
            phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout ?
                1
            :   phasedRelease?.userFraction
        :   null;
    const canUserEditUserFraction = useSelector((state: RootState) =>
        selectUserCanPerformAction(state, RunwayUserAction.UAUpdateRolloutPercentage, props.releaseId, null, ''),
    );

    const percentText = getRolloutPercentText(isGooglePlayStore, rolloutComplete, userFraction);

    const phasedReleaseText = capitalize(phasedReleaseDisplayTextForAppStoreType(appStoreType));

    React.useEffect(() => {
        if (haltGoogleStagedReleaseError) {
            const error = formatErrorMessageForDisplay('Error halting staged release', haltGoogleStagedReleaseError);
            globalMessage.error(error);
        }
    }, [haltGoogleStagedReleaseError]);

    React.useEffect(() => {
        if (haltGoogleStagedReleaseSuccess) {
            globalMessage.success('Staged rollout has been halted.');
        }
    }, [haltGoogleStagedReleaseSuccess]);

    React.useEffect(() => {
        if (completeGoogleStagedReleaseError) {
            const error = formatErrorMessageForDisplay(
                'Error rolling out to all users',
                completeGoogleStagedReleaseError,
            );
            globalMessage.error(error);
        }
    }, [completeGoogleStagedReleaseError]);

    React.useEffect(() => {
        if (completeGoogleStagedReleaseSuccess) {
            globalMessage.success('Your app has been rolled out to 100% of users.');
        }
    }, [completeGoogleStagedReleaseSuccess]);

    React.useEffect(() => {
        if (pauseApplePhasedReleaseError) {
            const error = formatErrorMessageForDisplay('Error pausing phased release', pauseApplePhasedReleaseError);
            globalMessage.error(error);
        }
    }, [pauseApplePhasedReleaseError]);

    React.useEffect(() => {
        if (pauseHuaweiPhasedReleaseSuccess) {
            globalMessage.success('Your phased release has been paused.');
        }
    }, [pauseHuaweiPhasedReleaseSuccess]);

    React.useEffect(() => {
        if (pauseHuaweiPhasedReleaseError) {
            const error = formatErrorMessageForDisplay('Error pausing phased release', pauseHuaweiPhasedReleaseError);
            globalMessage.error(error);
        }
    }, [pauseHuaweiPhasedReleaseError]);

    React.useEffect(() => {
        if (completeHuaweiPhasedReleaseSuccess) {
            globalMessage.success('Your app has been released to all users.');
        }
    }, [completeHuaweiPhasedReleaseSuccess]);

    React.useEffect(() => {
        if (completeHuaweiPhasedReleaseError) {
            const error = formatErrorMessageForDisplay(
                'Error releasing to all users',
                completeHuaweiPhasedReleaseError,
            );
            globalMessage.error(error);
        }
    }, [completeHuaweiPhasedReleaseError]);

    React.useEffect(() => {
        if (pauseApplePhasedReleaseSuccess) {
            globalMessage.success('Your phased release has been paused.');
        }
    }, [pauseApplePhasedReleaseSuccess]);

    React.useEffect(() => {
        if (completeApplePhasedReleaseError) {
            const error = formatErrorMessageForDisplay('Error releasing to all users', completeApplePhasedReleaseError);
            globalMessage.error(error);
        }
    }, [completeApplePhasedReleaseError]);

    React.useEffect(() => {
        if (completeApplePhasedReleaseSuccess) {
            globalMessage.success('Your app has been released to all users.');
        }
    }, [completeApplePhasedReleaseSuccess]);

    React.useEffect(() => {
        if (resumeApplePhasedReleaseError) {
            const error = formatErrorMessageForDisplay('Error resuming phased release', resumeApplePhasedReleaseError);
            globalMessage.error(error);
        }
    }, [resumeApplePhasedReleaseError]);

    React.useEffect(() => {
        if (resumeApplePhasedReleaseSuccess) {
            globalMessage.success('Your phased release has been resumed.');
        }
    }, [resumeApplePhasedReleaseSuccess]);

    React.useEffect(() => {
        if (startStagedRolloutError) {
            const error = formatErrorMessageForDisplay(
                'Error starting automated staged rollout',
                startStagedRolloutError,
            );
            globalMessage.error(error);
        }
    }, [startStagedRolloutError]);

    React.useEffect(() => {
        if (startStagedRolloutSuccess) {
            globalMessage.success('Automated staged rollout has started!');
        }
    }, [startStagedRolloutSuccess]);

    const resumePhasedReleaseIcon = <CaretRightOutlined style={{ color: runwayColors.statusGreen }} />;
    const resumeAllOption = {
        id: 'resumeAll',
        title: 'Resume rollout and all rollout automations',
    };

    const resumeSelectOption = {
        id: 'resumeSelect',
        title: 'Resume rollout and select automations...',
    };

    const resumeOptions = [resumeAllOption, resumeSelectOption];

    const resumeRolloutMenu = (
        <Menu
            onClick={(item) => {
                switch (item.key) {
                    case resumeAllOption.id:
                        setAutoSelectAutomations(true);
                        setStagedRolloutEditModalVisible(true);
                        break;
                    case resumeSelectOption.id:
                        setAutoSelectAutomations(false);
                        setStagedRolloutEditModalVisible(true);
                        break;
                    default:
                        console.log(`Unknown menu item ${item.key}`);
                }
            }}
            items={resumeOptions.map((option) => ({
                label: option.title,
                key: option.id,
                icon: resumePhasedReleaseIcon,
            }))}
        />
    );

    const needsAutomatedRolloutStart =
        (isGooglePlayStore || isHuaweiAppGallery) && automatedStagedRolloutEnabled && !rolloutStartedAt;
    const rolloutHasStarted = !!rolloutStartedAt;

    const isAutomatedPhasedRollout = isAppleAppStore ? hasPhasedReleaseState : automatedStagedRolloutEnabled;

    return (
        <div
            style={{
                padding: '18px',
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 12,
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space align="center">
                    <Typography.Text style={{ color: antGrey.grey7 }}>Rollout status</Typography.Text>
                    {hasPhasedReleaseState && rolloutHasStarted && (
                        <Tag style={tagStyle} color={phasedStateColor}>
                            {phasedStateStatusText}
                        </Tag>
                    )}
                </Space>
                {appStoreIntegrationId && <IntegrationIcon integrationId={appStoreIntegrationId} />}
            </div>
            {phasedRelease ?
                <Flex vertical gap={16}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24 }}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'baseline',
                                    gap: 6,
                                    maxWidth: 500,
                                }}
                            >
                                <Typography.Text
                                    style={{
                                        fontSize: 46,
                                        color: antGrey.grey8,
                                    }}
                                    ellipsis
                                >
                                    {isNaN(percentText) ? '--' : percentText}%
                                </Typography.Text>
                                <Typography.Text
                                    style={{
                                        fontSize: 12,
                                        color: antGrey.grey7,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        width: 'max-content',
                                    }}
                                    ellipsis
                                >
                                    of users
                                </Typography.Text>
                            </div>

                            {rolloutComplete && (
                                <Typography.Text style={{ fontSize: 14, color: antGrey.grey7, textAlign: 'right' }}>
                                    Your {phasedReleaseText.toLowerCase()} is <strong>complete.</strong>
                                </Typography.Text>
                            )}
                            {!rolloutComplete && isPhasedRollout && (
                                <PhasedReleaseDayText
                                    appId={appId}
                                    daysToPercent={daysToPercent}
                                    appStoreType={appStoreType}
                                    isPhasedReleasePaused={phasedReleasePaused}
                                    phasedReleaseDay={phasedRelease?.dayNumber ?? 0}
                                    phasedRelease={phasedRelease}
                                    textStyle={{ textAlign: 'left' }}
                                    androidStagedRolloutNotStarted={needsAutomatedRolloutStart}
                                    currentPercent={percentText}
                                />
                            )}
                        </div>
                    </div>
                    {phasedRelease && (
                        <PhasedReleaseChartBase
                            percentage={phasedRelease.phasedReleasePercent}
                            dayNumber={phasedRelease.dayNumber}
                            daysToPercent={daysToPercent}
                            isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                            wasHalted={isPhasedReleasePaused(phasedRelease.state)}
                            wasAccelerated={
                                phasedRelease.isManuallyAcceleratedPhasedRollout ||
                                phasedRelease.isRunwayAcceleratedRollout
                            }
                        />
                    )}
                    <div style={{ display: 'flex' }}>
                        {isAppleAppStore && (
                            <Flex vertical={false} gap={12} style={{ width: '100%' }}>
                                {!phasedReleasePaused && (
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAHaltRollout}
                                        icon={<PauseOutlined style={{ color: runwayColors.statusRed }} />}
                                        onClick={() => pauseApplePhasedRelease()}
                                        loading={pauseApplePhasedReleaseIsLoading}
                                        releaseId={releaseId}
                                        disabled={rolloutComplete}
                                        customDisallowedMessage="You do not have permission to pause phased releases"
                                        style={{ width: '100%' }}
                                    >
                                        Pause phased release
                                    </PermissionedActionButton>
                                )}
                                {phasedReleasePaused && (
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAResumeRollout}
                                        icon={resumePhasedReleaseIcon}
                                        onClick={() => resumeApplePhasedRelease()}
                                        overlay={resumeRolloutMenu}
                                        loading={resumeApplePhasedReleaseIsLoading}
                                        releaseId={releaseId}
                                        disabled={rolloutComplete}
                                        customDisallowedMessage="You do not have permission to resume rollouts"
                                        style={{ width: '100%' }}
                                    >
                                        Resume rollout
                                    </PermissionedActionButton>
                                )}
                                <Popconfirm
                                    title="Are you sure you want to release to all users now?"
                                    okText="Yes, release to all users now."
                                    placement="topRight"
                                    okButtonProps={{ danger: true }}
                                    onConfirm={() => completeApplePhasedRelease()}
                                >
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                        icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                                        loading={completeApplePhasedReleaseIsLoading}
                                        releaseId={releaseId}
                                        disabled={rolloutComplete}
                                        customDisallowedMessage="You do not have permission to release to 100% of users"
                                    >
                                        Release to all users now
                                    </PermissionedActionButton>
                                </Popconfirm>
                            </Flex>
                        )}
                        {isGooglePlayStore && (
                            <>
                                {needsAutomatedRolloutStart ?
                                    <PermissionedActionButton
                                        icon={<PlayCircleFilled />}
                                        releaseId={releaseId}
                                        onClick={startAutomatedStagedRollout}
                                        loading={startStagedRolloutIsLoading}
                                        type="primary"
                                    >
                                        Start automated rollout sequence
                                    </PermissionedActionButton>
                                :   <Flex vertical={false} gap={12} style={{ width: '100%' }}>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAHaltRollout}
                                            icon={<PauseOutlined style={{ color: runwayColors.statusRed }} />}
                                            onClick={() => haltGoogleStagedRollout()}
                                            loading={haltGoogleStagedReleaseIsLoading}
                                            disabled={phasedReleasePaused || rolloutComplete}
                                            releaseId={releaseId}
                                            customDisallowedMessage="You do not have permission to halt rollouts"
                                            style={{ width: '100%' }}
                                        >
                                            Halt rollout
                                        </PermissionedActionButton>
                                        <PermissionedActionButton
                                            actionType={
                                                phasedReleasePaused ?
                                                    RunwayUserAction.UAResumeRollout
                                                :   RunwayUserAction.UAUpdateRolloutPercentage
                                            }
                                            icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                                            onClick={() => setStagedRolloutEditModalVisible(true)}
                                            releaseId={releaseId}
                                            disabled={rolloutComplete}
                                            customDisallowedMessage={
                                                phasedReleasePaused ?
                                                    'You do not have permission to resume staged rollouts'
                                                :   'You do not have permission to edit staged rollouts'
                                            }
                                            style={{ width: '100%' }}
                                        >
                                            {phasedReleasePaused ? 'Resume staged rollout' : 'Edit staged rollout'}
                                        </PermissionedActionButton>
                                        <Popconfirm
                                            title="Are you sure you want to release to 100% now?"
                                            okText="Yes, release to 100% now."
                                            placement="topRight"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => completeGoogleStagedRollout()}
                                        >
                                            <PermissionedActionButton
                                                actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                                icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                                                loading={completeGoogleStagedReleaseIsLoading}
                                                releaseId={releaseId}
                                                disabled={rolloutComplete}
                                                customDisallowedMessage="You do not have permission to release to 100% of users"
                                                style={{ width: '100%' }}
                                            >
                                                Release to 100% now
                                            </PermissionedActionButton>
                                        </Popconfirm>
                                    </Flex>
                                }
                            </>
                        )}
                        {isHuaweiAppGallery && (
                            <>
                                {needsAutomatedRolloutStart ?
                                    <PermissionedActionButton
                                        icon={<PlayCircleFilled />}
                                        releaseId={releaseId}
                                        onClick={startAutomatedStagedRollout}
                                        loading={startStagedRolloutIsLoading}
                                        type="primary"
                                    >
                                        Start automated rollout sequence
                                    </PermissionedActionButton>
                                :   <Flex vertical={false} gap={12} style={{ width: '100%' }}>
                                        <PermissionedActionButton
                                            actionType={RunwayUserAction.UAHaltRollout}
                                            icon={<PauseOutlined style={{ color: runwayColors.statusRed }} />}
                                            onClick={() => haltHuaweiPhasedRelease()}
                                            loading={pauseHuaweiPhasedReleaseIsLoading}
                                            disabled={phasedReleasePaused || rolloutComplete}
                                            releaseId={releaseId}
                                            customDisallowedMessage="You do not have permission to pause phased releases"
                                            style={{ width: '100%' }}
                                        >
                                            Pause phased release
                                        </PermissionedActionButton>
                                        <PermissionedActionButton
                                            actionType={
                                                phasedReleasePaused ?
                                                    RunwayUserAction.UAResumeRollout
                                                :   RunwayUserAction.UAUpdateRolloutPercentage
                                            }
                                            icon={<PercentageOutlined style={{ color: runwayColors.info }} />}
                                            onClick={() => setStagedRolloutEditModalVisible(true)}
                                            releaseId={releaseId}
                                            disabled={rolloutComplete}
                                            customDisallowedMessage={
                                                phasedReleasePaused ?
                                                    'You do not have permission to resume phased releases'
                                                :   'You do not have permission to edit phased releases'
                                            }
                                            style={{ width: '100%' }}
                                        >
                                            {phasedReleasePaused ? 'Resume phased release' : 'Edit phased release'}
                                        </PermissionedActionButton>
                                        <Popconfirm
                                            title="Are you sure you want to release to 100% now?"
                                            okText="Yes, release to 100% now."
                                            placement="topRight"
                                            okButtonProps={{ danger: true }}
                                            onConfirm={() => completeHuaweiPhasedRelease()}
                                        >
                                            <PermissionedActionButton
                                                actionType={RunwayUserAction.UAUpdateRolloutPercentage}
                                                icon={<RiseOutlined style={{ color: runwayColors.statusGreen }} />}
                                                loading={completeHuaweiPhasedReleaseIsLoading}
                                                releaseId={releaseId}
                                                disabled={rolloutComplete}
                                                customDisallowedMessage="You do not have permission to release to 100% of users"
                                                style={{ width: '100%' }}
                                            >
                                                Release to 100% now
                                            </PermissionedActionButton>
                                        </Popconfirm>
                                        <RolloutActionButtons
                                            releaseId={releaseId}
                                            appId={props.appId}
                                            orgId={props.orgId}
                                            stepRunComponentProps={stepRunComponentProps}
                                        />
                                    </Flex>
                                }
                            </>
                        )}
                    </div>
                </Flex>
            :   <div
                    style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        width: '100%',
                        flexGrow: 1,
                    }}
                >
                    <Typography.Text
                        style={{
                            color: antGrey.grey6,
                        }}
                    >
                        No {phasedReleaseText.toLowerCase()} for this release.
                    </Typography.Text>
                </div>
            }
            {phasedRelease && !rolloutComplete && (
                <UpdateStagedRolloutModal
                    stepRunComponentProps={stepRunComponentProps}
                    autoSelectAutomations={autoSelectAutomations}
                    platform={platform}
                    appStoreType={appStoreType}
                    orgId={orgId}
                    appId={appId}
                    releaseId={releaseId}
                    open={stagedRolloutEditModalVisible}
                    isPaused={phasedReleasePaused}
                    userFraction={userFraction}
                    canEditUserFraction={canUserEditUserFraction}
                    onSuccess={() => setStagedRolloutEditModalVisible(false)}
                    onClose={() => setStagedRolloutEditModalVisible(false)}
                    phasedRelease={phasedRelease}
                    autoHaltUnstablePhasedReleasesAutomationId={props.autoHaltUnstablePhasedReleasesAutomationId}
                    autoPromoteStablePhasedReleasesAutomationId={props.autoPromoteStablePhasedReleasesAutomationId}
                />
            )}
        </div>
    );
};

interface PhasedReleaseCardProps extends ReleaseComponentProps {
    platform: AppPlatform;
    appStoreType: AppStoreType;
    phasedRelease?: PhasedRelease;
    appStoreIntegrationId?: IntegrationType;
    isMiniCardVersion?: boolean;
    rolloutStartedAt: Nullable<string>;
    autoHaltUnstablePhasedReleasesAutomationId: Nullable<string>;
    autoPromoteStablePhasedReleasesAutomationId: Nullable<string>;
    autoStagedRolloutAutomationId: Nullable<string>;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    isAutomatedPhasedRollout: boolean;
}

const PhasedReleaseCard: React.FC<PhasedReleaseCardProps> = (props: PhasedReleaseCardProps) => {
    const { platform, appStoreType, phasedRelease, appStoreIntegrationId } = props;

    const autoStagedRolloutAutomation = useSelector((state: RootState) =>
        selectAutomationById(state, props.appId, props.autoStagedRolloutAutomationId),
    ) as AutomationAutoStagedRollout;

    const daysToPercent = getRolloutDaysToPercentageFromAutomation(autoStagedRolloutAutomation);

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const isHuaweiAppGallery = getIsHuaweiAppGallery(appStoreType);

    const rolloutComplete = isPhasedReleaseComplete(phasedRelease);
    const phasedStateColor = colorForPhasedState(phasedRelease?.state);

    const userFraction =
        isGooglePlayStore || isHuaweiAppGallery ? phasedRelease?.userFraction
        : isAppPlatform(platform) ?
            phasedRelease?.isManuallyAcceleratedPhasedRollout || phasedRelease?.isRunwayAcceleratedRollout ?
                1
            :   phasedRelease?.userFraction
        :   null;

    const rolloutPercentage =
        isGooglePlayStore ?
            (
                rolloutComplete // when android release is complete show 100 because userFraction returns 0
            ) ?
                100
            :   convertAndroidStagedRolloutPercentage(userFraction)
        :   convertUserFraction(userFraction);

    const displayRolloutPercentage = displayStringForRolloutPercentage(rolloutPercentage);

    const displayText = phasedReleaseDisplayTextForAppStoreType(appStoreType);

    return props.isMiniCardVersion ?
            <MonitoringProgressCard
                isAutomatedPhasedRollout={props.isAutomatedPhasedRollout}
                title={capitalize(displayText)}
                integrationType={appStoreIntegrationId}
                progress={rolloutPercentage}
                value={displayRolloutPercentage}
                strokeColor={phasedStateColor}
                phasedRelease={phasedRelease}
                daysToPercent={daysToPercent}
                automatedByRunway={phasedRelease?.isRunwayAcceleratedRollout || phasedRelease?.isRunwayPaused}
                shouldApplyRestrictedTooltipForRollouts={false}
            />
        :   <PhasedReleaseCardFull {...props} />;
};

export default PhasedReleaseCard;

export const colorForPhasedState: (state: PhasedReleaseState) => string = (state) => {
    switch (state) {
        case GooglePlayStoreState.Halted:
        case AppleAppStorePhasedReleaseState.Paused:
            return runwayColors.statusRed;
        case GooglePlayStoreState.InProgress:
        case AppleAppStorePhasedReleaseState.Active:
            return runwayColors.primary;
        case GooglePlayStoreState.Completed:
        case AppleAppStorePhasedReleaseState.Complete:
            return runwayColors.statusGreen;
        case GooglePlayStoreState.Draft:
        case GooglePlayStoreState.StatusUnspecified:
        case AppleAppStorePhasedReleaseState.Inactive:
            return runwayColors.statusGrey;
        default:
            return runwayColors.statusGrey;
    }
};
